import { useNavigate, useSearchParams } from "react-router-dom";
import { Subtree } from "subtree.js/react";
import { pick } from "trunk.js";
import type { AppDeps } from "../../app-deps";
import { scannerLanding } from "../scan-card/scanner";
import { CustomerScansController } from "./customer-scans-controller";
import { CustomerScansPage } from "./customer-scans-page";

/** The selected customer lives in the URL; a different one gets a fresh controller through `deps`. */
export function CustomerScansRoute({ deps }: { deps: AppDeps }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const customerId = params.get("customer");
  // Where the scan-review screen returns after approve/reject: the exact view
  // the owner left, so the customer's scan list re-renders in place.
  const returnTo = customerId ? `/customer-scans?customer=${customerId}` : undefined;
  return (
    <Subtree
      key="customer-scans"
      deps={[customerId]}
      controller={() =>
        new CustomerScansController(
          { customerId },
          pick(
            deps,
            "client",
            "authHandler",
            "storeRepository",
            "providersRepository",
            "customersRepository",
            "pendingCartsRepository",
            "reviewQueueRepository",
            "stripeTerminal",
            "scanSessions",
          ),
          {
            showCustomer: (id) => setParams({ customer: id }),
            showAllCustomers: () => setParams({}),
            // In place of the address, or Back brings the nameless customer back.
            leaveCustomer: () => setParams({}, { replace: true }),
            editScan: (scanId) => navigate(`/customer-scans/${scanId}/edit`, { state: { returnTo } }),
            openCard: (cardId, quoteNote) => navigate(`/cards/${cardId}`, { state: { quoteNote } }),
            // Opened from the page Done lands on, so Done goes back to it rather than stacking another.
            scanFor: (id) =>
              navigate(`/scan?customer=${encodeURIComponent(id)}`, { state: { returnTo: scannerLanding(id) } }),
          },
        )
      }
    >
      <CustomerScansPage />
    </Subtree>
  );
}
